import React, { useEffect, useMemo, useState } from "react";
import { format, isAfter, isSameDay, isValid, parse, parseISO, startOfDay } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/global/base/button.tsx";
import { Calendar } from "@/global/base/calendar.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/global/base/popover.tsx";

const ISO = "yyyy-MM-dd";

/**
 * @param {unknown} value
 * @returns {Date | null}
 */
function toDateOrNull(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string") {
    const fromIso = parseISO(value);
    if (isValid(fromIso)) return fromIso;
    const fromSlash = parse(value, ISO, new Date());
    return isValid(fromSlash) ? fromSlash : null;
  }
  return null;
}

/**
 * Plain controlled date field: type in the input (yyyy-MM-dd) or pick from the calendar.
 * No react-hook-form — pass `value` and `onChange` only.
 *
 * @param {object} props
 * @param {Date | string | null | undefined} props.value
 * @param {(date: Date | null) => void} props.onChange
 * @param {string} [props.label]
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.required]
 * @param {string} [props.className]
 * @param {string} [props.inputClassName]
 * @param {string} [props.description]
 * @param {boolean} [props.allowFuture]
 * @param {boolean} [props.allowToday]
 * @param {string} [props['data-testid']]
 */
const SimpleDatePicker = ({
  value,
  onChange,
  label,
  id,
  name,
  readOnly = false,
  disabled = false,
  required = false,
  className = "",
  inputClassName = "",
  description,
  allowFuture = true,
  allowToday = true,
  "data-testid": dataTestId,
}) => {
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const dateValue = useMemo(() => toDateOrNull(value), [value]);

  const [month, setMonth] = useState(() => dateValue || new Date());

  useEffect(() => {
    if (dateValue) setMonth(dateValue);
  }, [dateValue]);

  const inputId = id || name || "simple-date-picker";

  const handleInputChange = (event) => {
    const raw = event.target.value;
    if (!raw) {
      onChange(null);
      setInvalid(false);
      return;
    }
    const parsed = parse(raw, ISO, new Date());
    if (isValid(parsed)) {
      onChange(parsed);
      setMonth(parsed);
      setInvalid(false);
    } else {
      onChange(null);
      setInvalid(true);
    }
  };

  const handleBlur = () => {
    if (!dateValue) {
      setInvalid(false);
      return;
    }
    const asString = format(dateValue, ISO);
    const parsed = parse(asString, ISO, new Date());
    if (isValid(parsed)) {
      setMonth(parsed);
      setInvalid(false);
    } else {
      setInvalid(true);
    }
  };

  const handleCalendarSelect = (next) => {
    if (next) {
      onChange(next);
      setInvalid(false);
      setMonth(next);
    } else {
      onChange(null);
    }
    setOpen(false);
  };

  const isDayDisabled = (day) => {
    const d = startOfDay(day);
    const today = startOfDay(new Date());
    if (!allowFuture && isAfter(d, today)) return true;
    if (!allowToday && isSameDay(d, today)) return true;
    return false;
  };

  if (readOnly && dateValue) {
    return (
      <div className={cn("space-y-1", className)}>
        {label ? (
          <p className="text-sm font-medium text-[#00567a]">
            {label}
            {required ? "*" : ""}
          </p>
        ) : null}
        <p className="text-base text-[#646464]">{format(dateValue, "dd MMM yyyy")}</p>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("space-y-1", className)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-medium text-[#00567a]">
          {label}
          {required ? "*" : ""}
        </label>
      ) : null}

      <div className="flex items-center gap-2">
        <input
          id={inputId}
          name={name}
          type="date"
          disabled={disabled || readOnly}
          placeholder="DD / MM / YYYY"
          data-testid={dataTestId}
          className={cn(
            "h-9 min-w-0 flex-1 border-b px-3 py-2 text-sm outline-none transition-colors",
            dateValue ? "text-black text-base" : "text-gray-400",
            invalid ? "border-red-500" : "border-gray-300",
            inputClassName
          )}
          value={dateValue ? format(dateValue, ISO) : ""}
          onChange={handleInputChange}
          onBlur={handleBlur}
        />

        <Popover open={open} onOpenChange={setOpen}>
          {/*
            Radix PopoverTrigger must receive a ref on the DOM trigger. The shared Button
            component does not forward refs, so asChild + Button breaks open/positioning.
          */}
          <PopoverTrigger
            type="button"
            className={cn(
              buttonVariants({ variant: "outline", size: "sm" }),
              "h-9 w-9 shrink-0 p-0 [&_svg]:pointer-events-auto"
            )}
            disabled={disabled || readOnly}
            aria-label="Open calendar"
          >
            <CalendarIcon className="h-4 w-4" />
          </PopoverTrigger>
          <PopoverContent
            className="z-[300] w-auto border bg-popover p-0 shadow-md"
            align="end"
            sideOffset={6}
          >
            <Calendar
              mode="single"
              selected={dateValue ?? undefined}
              onSelect={handleCalendarSelect}
              month={month}
              onMonthChange={setMonth}
              disabled={isDayDisabled}
            />
          </PopoverContent>
        </Popover>
      </div>

      {description ? (
        <p className="text-sm text-muted-foreground">{description}</p>
      ) : null}
    </div>
  );
};

export default SimpleDatePicker;
